import { onRole, type Block, type Lang, type Link, type Text } from '@ksa/model'
import { SiteLink, SiteText, hasText, useRender } from '../context.tsx'
import { hasCopyIn, otherLang, pathFor } from '../paths.ts'

type NavItem = { label: Text; link: Link }
type Props = { logo: Text; items: NavItem[]; ctaLabel: Text; ctaLink: Link; sticky: boolean }

/**
 * The site navigation.
 *
 * Nothing here says left or right — the row is `justify-content: space-between` on a direction-neutral
 * flex container, so it mirrors under RTL with no second rule and no variant. That is the whole of
 * "RTL is not a variant" in one component.
 *
 * ⚠️ IT IS A `<nav>` WITH A LABEL. A published page has exactly one navigation landmark and a screen
 * reader user jumps to it by name; an unlabelled `<nav>` is announced as "navigation" with no way to
 * tell it from the footer's links.
 */
export function Navbar({ block }: { block: Block }) {
  const { site, lang } = useRender()
  const p = block.props as unknown as Props
  const centered = block.variant === 'centered'
  const minimal = block.variant === 'minimal'

  // Unused today — kept as the one place a navbar would read a ground if it ever offered one, so the
  // pairing rule is applied rather than reinvented.
  void onRole

  return (
    <nav
      className={`s-nav${p.sticky ? ' s-nav--sticky' : ''}`}
      aria-label={lang === 'ar' ? 'التنقّل الرئيسي' : 'Main'}
    >
      {/*
        ⚠️ THE CLASS IS ON THE LINK, NOT ON A SPAN INSIDE IT, AND THAT DISTINCTION WAS A VISIBLE BUG.
        An underline drawn by an ancestor propagates to its descendants and CANNOT be removed by
        `text-decoration: none` further down — so an unclassed `<a>` around a styled span produced the
        site's own brand colour on the text with the browser's default link underline, in the default
        link colour, drawn through it. On every published page, on the business's own name.
      */}
      <SiteLink link={{ kind: 'page', pageId: site.pages[0]?.id ?? '' }} className="s-nav__logo">
        <span>
          <SiteText value={p.logo} />
        </span>
      </SiteLink>

      {!minimal && (
        <div className={`s-nav__links${centered ? ' s-nav__links--centered' : ''}`}>
          {(p.items ?? []).map((item, i) => (
            <SiteLink key={i} link={item.link} className="s-nav__link">
              <SiteText value={item.label} />
            </SiteLink>
          ))}
          {hasText(p.ctaLabel) && (
            <SiteLink link={p.ctaLink} className="s-btn">
              <SiteText value={p.ctaLabel} />
            </SiteLink>
          )}
        </div>
      )}

      {/*
        ⚠️ LAST, SO `space-between` PUTS IT IN THE CORNER OPPOSITE THE SITE'S NAME. What keeps it on
        the NAME's row rather than on a row of its own is `.s-nav__links`' basis — see `site.css`;
        this component only decides the order.

        > ⚠️ **IT HAS BEEN IN THREE PLACES IN THREE COMMITS, AND ONLY ONE MOVE WAS A CORRECTION.** As
        > a third child with `flex: 1 1 auto` links beside it, it wrapped onto a second row of navbar
        > alone on a 412 px phone — a tenth of the screen for two characters. Moving it inside the
        > links group fixed the wrap and was the wrong home; beside the name was the next try; the
        > corner opposite the name is the ask. The wrap was the real constraint all along, and it is
        > solved in the stylesheet now rather than by hiding the pill among the links.

        ⚠️ OUTSIDE THE `minimal` GUARD, BECAUSE THE PILL IS NOT NAVIGATION THE CUSTOMER CHOSE TO HIDE.
        `minimal` drops the links and the call to action; both languages are published whatever
        variant is picked, so a page no visitor can reach would be a page written for nobody.
      */}
      <LanguagePill />
    </nav>
  )
}

const NAME: Record<Lang, string> = { en: 'English', ar: 'العربية' }

/**
 * The other language, as a pill in the navigation.
 *
 * ⚠️ **BOTH LANGUAGES ARE ALWAYS PUBLISHED AND, UNTIL THIS EXISTED, ONLY A SEARCH ENGINE COULD REACH
 * THE SECOND ONE.** `renderSite` writes every page twice and the document head carries `hreflang`
 * alternates — so an Arabic customer's English pages were on the internet, indexed, and unreachable
 * by anybody actually looking at the site. Half of what this product is for was a URL you had to
 * know to type.
 *
 * ⚠️ IT IS A PLAIN `<a>` TO A REAL URL, NOT A CONTROL THAT SWITCHES SOMETHING. There is no
 * JavaScript on a published page (§2.7) and there must not be: the two languages are two documents,
 * so the switch is a navigation and the browser already knows how to do those. It is also why this
 * survives with the page cached, shared, or opened with scripting off.
 *
 * ⚠️ IT LINKS TO *THIS* PAGE, NOT TO THE HOME PAGE. Sending somebody reading the treatments page
 * back to the front door is the version of this that loses their place — and it is the version you
 * get for free by linking to `/en/`, which is why the path is computed rather than written.
 *
 * ⚠️ IT SHOWS THE LANGUAGE YOU WOULD GET, IN THAT LANGUAGE'S OWN SCRIPT. «EN» on an Arabic page,
 * «ع» on an English one. A pill that said the language you are already reading would be a label
 * rather than a control, and it is the mistake that makes half of these ambiguous. `lang` on the
 * element is what stops a screen reader announcing «ع» in an English voice.
 */
function LanguagePill() {
  const { site, page, lang } = useRender()
  const other = otherLang(lang)

  /*
   * ⚠️ ABSENT RATHER THAN DISABLED WHEN THERE IS NOTHING THERE. A stranger's site is written in one
   * language on purpose (§5.15) and the rest is filled in later, so on a new site the other language
   * is genuinely empty — and a pill leading to a blank page is worse than no pill, because the
   * visitor concludes the site is broken rather than that it is in one language.
   */
  if (!hasCopyIn(page, other)) return null

  const index = site.pages.findIndex((p) => p.id === page.id)
  const href = pathFor(site, page, other, index < 0 ? 0 : index)

  return (
    <a
      className="s-nav__lang"
      href={href}
      lang={other}
      hrefLang={other}
      // The full name for anybody who cannot see that this is a two-letter pill.
      aria-label={NAME[other]}
    >
      {other === 'ar' ? 'ع' : 'EN'}
    </a>
  )
}
